/**
 * ========================================
 * Spot Management Page - 스팟 관리
 * ========================================
 *
 * 기능:
 * - 스팟 목록 조회 (테이블)
 * - 구글맵으로 스팟 위치 확인
 * - 지도 클릭으로 새 스팟 생성
 * - 스팟 수정/삭제
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  HiLocationMarker,
  HiPlus,
  HiPencil,
  HiTrash,
  HiLogout,
  HiSparkles,
  HiX,
  HiViewGrid,
  HiMap,
  HiChartBar,
  HiCog,
  HiLockClosed
} from 'react-icons/hi';
import { spotAPI, adminAuth, gradeAPI } from '../services/api.service';
import GoogleMapContainer from '../components/GoogleMapContainer';
import SensorDataGrid from '../components/SensorDataGrid';
import { formatMeasurement } from '../utils/measurementUtils';
import ChangePasswordModal from '../components/ChangePasswordModal';

export default function SpotManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [mapClickedLocation, setMapClickedLocation] = useState(null);
  const [selectedSpotOnMap, setSelectedSpotOnMap] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  // URL 쿼리 파라미터에서 탭 정보 읽기
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => {
    // 초기 렌더링 시 URL 파라미터 확인
    return tabFromUrl === 'sensorData' ? 'sensorData' : 'spots';
  }); // 'spots' or 'sensorData'

  // URL 쿼리 파라미터 변경 시 탭 업데이트
  useEffect(() => {
    const tab = searchParams.get('tab');
    const newTab = tab === 'sensorData' ? 'sensorData' : 'spots';
    if (activeTab !== newTab) {
      setActiveTab(newTab);
    }
  }, [searchParams, activeTab]);

  // 위경도 유효 범위 상수
  const LAT_MIN = -90, LAT_MAX = 90, LNG_MIN = -180, LNG_MAX = 180;

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // 스팟 목록 조회
  const { data: spotsData, isLoading } = useQuery({
    queryKey: ['spots'],
    queryFn: () => spotAPI.getSpots({ limit: 100 }),
  });

  // 등급 판정 기준 조회
  const { data: gradeRulesData } = useQuery({
    queryKey: ['gradeRules'],
    queryFn: gradeAPI.getGradeRules,
  });

  const gradeRules = gradeRulesData?.data?.rules || [];

  // 스팟 생성 Mutation
  const createMutation = useMutation({
    mutationFn: spotAPI.createSpot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spots'] });
      setIsFormOpen(false);
      reset();
      alert('스팟이 생성되었습니다.');
    },
    onError: (error) => {
      alert('스팟 생성 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  // 스팟 수정 Mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => spotAPI.updateSpot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spots'] });
      setIsFormOpen(false);
      setEditingSpot(null);
      reset();
      alert('스팟이 수정되었습니다.');
    },
    onError: (error) => {
      alert('스팟 수정 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  // 스팟 삭제 Mutation
  const deleteMutation = useMutation({
    mutationFn: spotAPI.deleteSpot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spots'] });
      alert('스팟이 삭제되었습니다.');
    },
    onError: (error) => {
      alert('스팟 삭제 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  // 지도 클릭 핸들러 (신규 스팟 생성 시 위치 선택)
  const handleMapClick = (location) => {
    setMapClickedLocation(location);
    setIsFormOpen(true);
    setEditingSpot(null);
    reset({
      latitude: location.latitude.toFixed(8),
      longitude: location.longitude.toFixed(8),
      geofence_radius_meters: 50,
    });
  };

  // 마커 클릭 핸들러
  const handleMarkerClick = (spot) => {
    setSelectedSpotOnMap(spot);
  };

  // 폼 제출
  const onSubmit = (data) => {
    const spotData = {
      ...data,
      latitude: parseFloat(data.latitude),
      longitude: parseFloat(data.longitude),
      nanotesla_value: data.nanotesla_value ? parseFloat(data.nanotesla_value) : null,
      geofence_radius_meters: parseInt(data.geofence_radius_meters) || 50,
    };

    if (editingSpot) {
      updateMutation.mutate({ id: editingSpot.uuid, data: spotData });
    } else {
      createMutation.mutate(spotData);
    }
  };

  // 스팟 편집
  const handleEdit = (spot) => {
    setEditingSpot(spot);
    setIsFormOpen(true);
    setSelectedSpotOnMap(spot);

    reset({
      spot_name: spot.spot_name,
      latitude: spot.latitude,
      longitude: spot.longitude,
      magnetic_field_strength: spot.magnetic_field_strength,
      nanotesla_value: spot.nanotesla_value || '',
      geofence_radius_meters: spot.geofence_radius_meters || 50,
      short_description: spot.short_description || '',
      detailed_benefits: spot.detailed_benefits || '',
      address: spot.address || '',
    });
  };

  // 스팟 삭제
  const handleDelete = (spot) => {
    if (confirm(`"${spot.spot_name}" 스팟을 삭제하시겠습니까?`)) {
      deleteMutation.mutate(spot.uuid);
    }
  };

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/login');
  };

  const spots = spotsData?.data?.spots || [];

  // ESC 키로 모달 닫기 기능 제거 (닫기 버튼으로만 닫히도록)

  return (
    <div className="min-h-screen bg-theme-background">
      <style>{`
        /* 커스텀 스크롤바 스타일 */
        .spot-list-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .spot-list-scroll::-webkit-scrollbar-track {
          background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
          border-radius: 10px;
          margin: 8px 0;
        }
        .spot-list-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: all 0.3s ease;
        }
        .spot-list-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
        }
        .spot-list-scroll::-webkit-scrollbar-thumb:active {
          background: linear-gradient(180deg, #4338ca 0%, #6d28d9 50%, #be185d 100%);
        }
        /* Firefox 스크롤바 스타일 */
        .spot-list-scroll {
          scrollbar-width: thin;
          scrollbar-color: #6366f1 #f1f5f9;
        }
      `}</style>
      {/* Header */}
      <motion.header
        className="bg-gradient-to-r from-indigo-600 via-pink-600 to-indigo-600 backdrop-blur-xl shadow-2xl border-b border-white/20"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-5">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  className="p-1 bg-white/20 backdrop-blur-md rounded-full shadow-lg border-2 border-white/40 relative overflow-hidden"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src="/Frame 34.png" 
                    alt="시스템 아이콘" 
                    className="w-10 h-10 object-cover rounded-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.icon-fallback');
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <HiSparkles className="w-10 h-10 text-white icon-fallback hidden rounded-full" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white tracking-tight drop-shadow-lg">
                    팔팔구구 <span className="bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">메타보맵</span>
                  </h1>
                  <p className="text-sm font-medium text-white/90 mt-0.5">스팟 관리</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="text-sm font-bold text-white drop-shadow-md">
                {adminAuth.getCurrentAdmin()?.username}
              </span>
              <motion.button
                onClick={() => setIsPasswordModalOpen(true)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLockClosed className="w-4 h-4" />
                비밀번호 변경
              </motion.button>
              <motion.button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLogout className="w-4 h-4" />
                로그아웃
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-8">
        {/* Navigation Tabs */}
        <motion.div
          className="mb-8 bg-theme-surface backdrop-blur-lg rounded-2xl shadow-lg border-2 border-theme-primary/20 p-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiViewGrid className="w-5 h-5" />
              대시보드
            </motion.button>
            <motion.button
              onClick={() => {
                setActiveTab('spots');
                setSearchParams({});
              }}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeTab === 'spots'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiMap className="w-5 h-5" />
              스팟 관리
            </motion.button>
            <motion.button
              onClick={() => {
                setActiveTab('sensorData');
                setSearchParams({ tab: 'sensorData' });
              }}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeTab === 'sensorData'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiChartBar className="w-5 h-5" />
              스팟별 측정자료 조회
            </motion.button>
            <motion.button
              onClick={() => navigate('/grade-settings')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiCog className="w-5 h-5" />
              등급 판정 기준
            </motion.button>
          </nav>
        </motion.div>

        {activeTab === 'spots' ? (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Left: Spot List - 40% */}
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-theme-surface backdrop-blur-lg rounded-xl shadow-lg border border-theme-primary/20">
              <div className="px-4 py-4 border-b border-theme-primary/20 flex justify-between items-center">
                <h3 className="text-lg font-medium text-theme-text">
                  스팟 목록 ({spots.length})
                </h3>
                <motion.button
                  onClick={() => {
                    setIsFormOpen(true);
                    setEditingSpot(null);
                    reset({
                      geofence_radius_meters: 50,
                    });
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-theme-primary to-theme-secondary text-white text-sm font-medium rounded-lg hover:from-indigo-500 hover:to-pink-500 flex items-center gap-2 shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <HiPlus className="w-4 h-4" />
                  새 스팟
                </motion.button>
              </div>

              <div className="overflow-y-auto max-h-[600px] p-4 spot-list-scroll">
                {isLoading ? (
                  <div className="p-8 text-center text-theme-text-secondary">로딩 중...</div>
                ) : spots.length === 0 ? (
                  <div className="p-8 text-center text-theme-text-secondary">
                    스팟이 없습니다. 지도를 클릭하여 추가하세요.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {spots.map((spot, index) => (
                      <motion.div
                        key={spot.uuid}
                        className={`bg-theme-surface ${
                          selectedSpotOnMap?.uuid === spot.uuid
                            ? 'border-2 border-theme-primary'
                            : 'border border-theme-primary/20'
                        } rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-lg`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -2, scale: 1.01 }}
                        onClick={() => handleMarkerClick(spot)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <HiLocationMarker className={`w-5 h-5 ${
                                spot.magnetic_field_strength === 'extreme'
                                  ? 'text-red-800'
                                  : spot.magnetic_field_strength === 'very_strong' || spot.magnetic_field_strength === 'strong'
                                  ? 'text-red-400'
                                  : spot.magnetic_field_strength === 'medium'
                                  ? 'text-yellow-400'
                                  : 'text-blue-400'
                              }`} />
                              <h4 className="text-base font-semibold text-theme-text">
                                {spot.spot_name}
                              </h4>
                              <span
                                className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                                  spot.magnetic_field_strength === 'extreme'
                                    ? 'bg-gradient-to-r from-red-800 to-red-900 text-white'
                                    : spot.magnetic_field_strength === 'very_strong'
                                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white'
                                    : spot.magnetic_field_strength === 'strong'
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                                    : spot.magnetic_field_strength === 'medium'
                                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white'
                                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                                } shadow-sm`}
                              >
                                {spot.magnetic_field_strength === 'extreme'
                                  ? '극강'
                                  : spot.magnetic_field_strength === 'very_strong'
                                  ? '매우강함'
                                  : spot.magnetic_field_strength === 'strong'
                                  ? '강함'
                                  : spot.magnetic_field_strength === 'medium'
                                  ? '중간'
                                  : '약함'}
                              </span>
                            </div>
                            {spot.short_description && (
                              <p className="text-sm text-theme-text-secondary mb-2 line-clamp-2">
                                {spot.short_description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              {spot.nanotesla_value && (
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="text-theme-primary font-semibold">측정값:</span>
                                  <span className="text-theme-text font-mono">{formatMeasurement(spot.nanotesla_value, true)}</span>
                                </div>
                              )}
                              {spot.address && (
                                <p className="text-xs text-theme-text-secondary line-clamp-1">
                                  {spot.address}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-theme-text-secondary font-mono mt-1">
                              {parseFloat(spot.latitude).toFixed(6)}, {parseFloat(spot.longitude).toFixed(6)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(spot);
                              }}
                              className="p-2 bg-theme-primary/20 text-theme-primary rounded-lg hover:bg-theme-primary/30 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="수정"
                            >
                              <HiPencil className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(spot);
                              }}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="삭제"
                            >
                              <HiTrash className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right: Google Map - 60% */}
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <GoogleMapContainer
              spots={spots}
              onMapClick={handleMapClick}
              selectedSpot={selectedSpotOnMap}
              onMarkerClick={handleMarkerClick}
            />
          </motion.div>
        </div>
        ) : (
          <SensorDataGrid />
        )}

        {/* Form Modal */}
        {isFormOpen && (
          <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop (클릭해도 닫히지 않음) */}
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
              
              {/* Modal Content */}
              <motion.div
                className="relative bg-theme-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-theme-primary/20"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="px-6 py-4 border-b border-theme-primary/20 bg-theme-surface flex items-center justify-between">
                  <h3 className="text-xl font-bold text-theme-text">
                    {editingSpot ? '스팟 수정' : '새 스팟 추가'}
                  </h3>
                  <motion.button
                    onClick={() => {
                      setIsFormOpen(false);
                      setEditingSpot(null);
                      reset();
                    }}
                    className="p-2 text-theme-text-secondary hover:text-theme-text rounded-lg hover:bg-theme-background transition-colors"
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <HiX className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Form Content */}
                <div className="overflow-y-auto flex-1 p-6">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="spot-form">
                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      스팟명 *
                    </label>
                    <input
                      {...register('spot_name', { required: true })}
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        위도 * (-90 ~ 90)
                      </label>
                      <input
                        {...register('latitude', {
                          required: '위도는 필수입니다',
                          valueAsNumber: true,
                          validate: (v) => {
                            if (v === '' || v === undefined || (typeof v === 'number' && Number.isNaN(v)))
                              return '위도는 필수입니다';
                            const n = Number(v);
                            if (n < -90 || n > 90) return '위도는 -90 ~ 90 사이여야 합니다';
                            return true;
                          },
                        })}
                        type="number"
                        step="0.00000001"
                        min={LAT_MIN}
                        max={LAT_MAX}
                        placeholder="-90 ~ 90"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-theme-background text-theme-text ${
                          errors.latitude ? 'border-red-500 focus:ring-red-500' : 'border-theme-primary/30 focus:ring-theme-primary'
                        } focus:ring-2 focus:border-transparent transition-all`}
                      />
                      {errors.latitude && (
                        <p className="text-xs text-red-500 mt-1">{errors.latitude.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        경도 * (-180 ~ 180)
                      </label>
                      <input
                        {...register('longitude', {
                          required: '경도는 필수입니다',
                          valueAsNumber: true,
                          validate: (v) => {
                            if (v === '' || v === undefined || (typeof v === 'number' && Number.isNaN(v)))
                              return '경도는 필수입니다';
                            const n = Number(v);
                            if (n < -180 || n > 180) return '경도는 -180 ~ 180 사이여야 합니다';
                            return true;
                          },
                        })}
                        type="number"
                        step="0.00000001"
                        min={LNG_MIN}
                        max={LNG_MAX}
                        placeholder="-180 ~ 180"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-theme-background text-theme-text ${
                          errors.longitude ? 'border-red-500 focus:ring-red-500' : 'border-theme-primary/30 focus:ring-theme-primary'
                        } focus:ring-2 focus:border-transparent transition-all`}
                      />
                      {errors.longitude && (
                        <p className="text-xs text-red-500 mt-1">{errors.longitude.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        자기장 강도 (등급 판정 기준) *
                      </label>
                      <select
                        {...register('magnetic_field_strength', { required: true })}
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                      >
                        <option value="">선택하세요</option>
                        {gradeRules.length > 0 ? (
                          // 등급 판정 기준에서 가져온 옵션
                          gradeRules
                            .sort((a, b) => {
                              // 등급 순서 정렬: weak -> medium -> strong -> very_strong -> extreme
                              const order = { weak: 1, medium: 2, strong: 3, very_strong: 4, extreme: 5 };
                              return (order[a.grade] || 99) - (order[b.grade] || 99);
                            })
                            .map((rule) => {
                              const minUt = rule.min_ut_threshold !== null ? Math.round(rule.min_ut_threshold) : null;
                              const maxUt = rule.max_ut_threshold !== null ? Math.round(rule.max_ut_threshold) : null;
                              return (
                                <option key={rule.grade} value={rule.grade}>
                                  {rule.grade_display} {minUt !== null && maxUt !== null && `(${minUt}~${maxUt} µT)`}
                                </option>
                              );
                            })
                        ) : (
                          // 등급 판정 기준이 없을 경우 기본 옵션 (하위 호환성)
                          <>
                            <option value="weak">약함 (Weak)</option>
                            <option value="medium">중간 (Medium)</option>
                            <option value="strong">강함 (Strong)</option>
                          </>
                        )}
                      </select>
                      {gradeRules.length === 0 && (
                        <p className="text-xs text-yellow-500 mt-1">
                          등급 판정 기준이 설정되지 않았습니다. 등급 판정 기준을 먼저 설정해주세요.
                        </p>
                      )}
                      {gradeRules.length > 0 && (
                        <p className="text-xs text-theme-text-secondary mt-1">
                          등급 판정 기준에서 선택한 등급이 스팟의 자기장 강도로 설정됩니다.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        측정값 (nT)
                      </label>
                      <input
                        {...register('nanotesla_value')}
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      Geofence 반경 (m)
                    </label>
                    <input
                      {...register('geofence_radius_meters')}
                      type="number"
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      주소
                    </label>
                    <input
                      {...register('address')}
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      간단 설명
                    </label>
                    <textarea
                      {...register('short_description')}
                      rows="2"
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      상세 효능
                    </label>
                    <textarea
                      {...register('detailed_benefits')}
                      rows="3"
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                    />
                  </div>
                  </form>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-theme-primary/20 bg-theme-surface flex space-x-3">
                    <motion.button
                      type="button"
                      onClick={() => {
                        setIsFormOpen(false);
                        setEditingSpot(null);
                        reset();
                      }}
                      className="flex-1 py-3 bg-theme-background text-theme-text-secondary font-medium rounded-lg hover:bg-theme-background/80 transition-colors border border-theme-primary/20"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      취소
                    </motion.button>
                    <motion.button
                      type="submit"
                      form="spot-form"
                      className="flex-1 py-3 bg-gradient-to-r from-theme-primary to-theme-secondary text-white font-medium rounded-lg hover:from-indigo-500 hover:to-pink-500 transition-all shadow-lg"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {editingSpot ? '수정하기' : '생성하기'}
                    </motion.button>
                  </div>
                </motion.div>
            </motion.div>
            )}
      </main>

      {/* 비밀번호 변경 모달 */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}
